import { useMemo, useState, useContext } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { RefreshIcon } from "@heroicons/react/outline";
import { fetchPickingTasksToBreak } from "../../services";
import { NewTable, PageView, PageTopBar, FormattedDate, DialogView } from "../../components";
import { UserContext } from "../../hooks/UserContext";

const T = "picking_tasks_to_break";
const PERMISSION_KEY = "resolve_container_breaks";
const DEFAULT_PER_PAGE = 25;
const PIVOTS = { PRODUCT: "product", ORDER: "order" };

// Multi-value (bulk) filters: the operator can enter several SKUs/UPCs or
// several order numbers at once; a row passes if it matches ANY entered token.
const initialFiltersData = (pivot) =>
    pivot === PIVOTS.PRODUCT
        ? [{
            key: "sku_upc",
            label: `${T}.filters.sku_upc`,
            placeholder: `${T}.filters.sku_upc`,
            data: { visible: false, type: "bulk", value: [] },
        }]
        : [{
            key: "order_number",
            label: `${T}.headers.order_number`,
            placeholder: `${T}.headers.order_number`,
            data: { visible: false, type: "bulk", value: [] },
        }];

const buildInitialFilters = (pivot, orderNumbers = []) => {
    const filters = initialFiltersData(pivot);
    if (pivot === PIVOTS.ORDER && orderNumbers.length > 0) {
        return filters.map((f) =>
            f.key === "order_number" ? { ...f, data: { ...f.data, visible: true, value: orderNumbers } } : f
        );
    }
    return filters;
};

// Deep link (e.g. from the picker-assignment block): ?pivot=order&order_numbers=A,B
const parseDeepLink = (search) => {
    const params = new URLSearchParams(search);
    const pivot = params.get("pivot") === PIVOTS.ORDER ? PIVOTS.ORDER : PIVOTS.PRODUCT;
    const raw = params.get("order_numbers");
    const orderNumbers = raw ? raw.split(",").map((s) => s.trim()).filter(Boolean) : [];
    return { pivot, orderNumbers };
};

const filterTokens = (filter) => {
    const raw = Array.isArray(filter.data.value) ? filter.data.value : [filter.data.value];
    return raw.map((v) => String(v ?? "").toLowerCase().trim()).filter(Boolean);
};

const rowMatchesFilter = (row, filter, pivot) => {
    const tokens = filterTokens(filter);
    if (tokens.length === 0) return true;
    if (pivot === PIVOTS.PRODUCT && filter.key === "sku_upc") {
        const haystack = [...(row.skus || []), ...(row.upcs || [])].map((s) => String(s).toLowerCase());
        return tokens.some((tok) => haystack.some((h) => h.includes(tok)));
    }
    if (pivot === PIVOTS.ORDER && filter.key === "order_number") {
        const on = String(row.order_number ?? "").toLowerCase();
        return tokens.some((tok) => on.includes(tok));
    }
    return true;
};

const sortValue = (item, key) => {
    const v = item[key];
    return v == null ? "" : v;
};

// Regroups the product-grouped payload by order_id (no extra backend call), so
// operations can target a specific high-priority order instead of a product.
const buildOrderRows = (products) => {
    const map = new Map();
    products.forEach((p) => {
        (p.orders || []).forEach((o) => {
            let entry = map.get(o.order_id);
            if (!entry) {
                entry = { order_id: o.order_id, order_number: o.order_number, total_pieces: 0, products: [], _locations: new Set() };
                map.set(o.order_id, entry);
            }
            const locs = [...new Set((o.items || []).map((i) => i.location).filter(Boolean))].sort();
            entry.products.push({
                product_id: p.product_id,
                product_name: p.product_name,
                skus: p.skus || [],
                upcs: p.upcs || [],
                pieces_needed: o.pieces_needed,
                breakable_stock_available: p.breakable_stock_available,
                items: o.items || [],
                locations: locs,
            });
            entry.total_pieces += o.pieces_needed || 0;
            locs.forEach((l) => entry._locations.add(l));
        });
    });
    return [...map.values()].map(({ _locations, ...rest }) => ({
        ...rest,
        products_count: rest.products.length,
        locations: [..._locations].sort(),
    }));
};

const BreakableBadge = ({ breakable, t }) => (
    <span
        className={`inline-block rounded-full py-1 px-2.5 text-xs font-semibold ${
            breakable ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
        }`}
    >
        {breakable ? t(`${T}.status.breakable`) : t(`${T}.status.not_breakable`)}
    </span>
);

const LocationsCell = ({ locations }) => {
    if (!locations || locations.length === 0) return <span className="text-gray-400">-</span>;
    return <span className="whitespace-normal">{locations.join(", ")}</span>;
};

const ProductNameCell = ({ product }) => (
    <div>
        <div className="font-medium text-gray-900">{product.product_name}</div>
        {product.skus?.length > 0 && (
            <div className="text-xs text-gray-500">SKU: {product.skus.join(", ")}</div>
        )}
        {product.upcs?.length > 0 && (
            <div className="text-xs text-gray-500">UPC: {product.upcs.join(", ")}</div>
        )}
    </div>
);

export const PickingTasksToBreakContainer = () => {
    const { i18n } = useTranslation();
    const t = i18n.t.bind(i18n);
    const { user } = useContext(UserContext);
    const { warehouse_id } = useParams();
    const warehouseId = isNaN(parseInt(warehouse_id)) ? null : parseInt(warehouse_id);
    const hasPermission = !!user?.permissions?.[PERMISSION_KEY];

    const { search } = useLocation();
    const deepLink = useMemo(() => parseDeepLink(search), [search]);

    const [pivot, setPivot] = useState(deepLink.pivot);
    const [filtersData, setFiltersData] = useState(() => buildInitialFilters(deepLink.pivot, deepLink.orderNumbers));
    const [appliedFilters, setAppliedFilters] = useState(() => buildInitialFilters(deepLink.pivot, deepLink.orderNumbers));
    const [sortBy, setSortBy] = useState([]);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
    const [detail, setDetail] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);

    const { data, isLoading, isError, isFetching, error, refetch } = useQuery(
        ["picking_tasks_to_break", warehouseId],
        () => fetchPickingTasksToBreak({ warehouse_id: warehouseId }),
        { enabled: !!warehouseId && hasPermission, refetchOnWindowFocus: true }
    );

    const products = useMemo(() => data?.products || [], [data]);
    const orderRows = useMemo(() => buildOrderRows(products), [products]);

    const summaryProducts = products.length;
    const summaryOrders = orderRows.length;

    const productColumns = useMemo(() => [
        { Header: t(`${T}.headers.product`), accessor: "product_name" },
        { Header: t(`${T}.headers.pieces_to_release`), accessor: "pieces_needed" },
        { Header: t(`${T}.headers.affected_orders`), accessor: "affected_orders" },
        { Header: t(`${T}.headers.oldest_order`), accessor: "oldest_order_at" },
        { Header: t(`${T}.headers.locations_reserved`), accessor: "locations", disableSortBy: true, disableWrap: false },
        { Header: t(`${T}.headers.status`), accessor: "status", disableSortBy: true },
    ], [i18n]); // eslint-disable-line react-hooks/exhaustive-deps

    const orderColumns = useMemo(() => [
        { Header: t(`${T}.headers.order_number`), accessor: "order_number" },
        { Header: t(`${T}.headers.total_pieces`), accessor: "total_pieces" },
        { Header: t(`${T}.headers.products_count`), accessor: "products_count" },
        { Header: t(`${T}.headers.locations_reserved`), accessor: "locations", disableSortBy: true, disableWrap: false },
    ], [i18n]); // eslint-disable-line react-hooks/exhaustive-deps

    // The endpoint returns a plain object (no pagination meta), so filtering,
    // sorting and pagination run client-side over the in-memory list.
    const { pageRows, paginationMeta } = useMemo(() => {
        const source = pivot === PIVOTS.PRODUCT ? products : orderRows;

        const activeFilters = appliedFilters.filter((f) => f.data.visible);
        const filtered = source.filter((row) =>
            activeFilters.every((f) => rowMatchesFilter(row, f, pivot))
        );

        const defaultKey = pivot === PIVOTS.PRODUCT ? "affected_orders" : "total_pieces";
        const sortKey = sortBy.length > 0 ? sortBy[0].id : defaultKey;
        const desc = sortBy.length > 0 ? sortBy[0].desc : true;
        const sorted = [...filtered].sort((a, b) => {
            const av = sortValue(a, sortKey);
            const bv = sortValue(b, sortKey);
            if (typeof av === "number" && typeof bv === "number") return av - bv;
            return String(av).localeCompare(String(bv), undefined, { numeric: true });
        });
        if (desc) sorted.reverse();

        const totalCount = sorted.length;
        const totalPages = Math.max(1, Math.ceil(totalCount / perPage));
        const currentPage = Math.min(page, totalPages);
        const slice = sorted.slice((currentPage - 1) * perPage, currentPage * perPage);

        const rows = pivot === PIVOTS.PRODUCT
            ? slice.map((p) => ({
                object: { id: p.product_id },
                _raw: p,
                product_name: <ProductNameCell product={p} />,
                pieces_needed: p.pieces_needed,
                affected_orders: p.affected_orders,
                oldest_order_at: p.oldest_order_at ? <FormattedDate date={p.oldest_order_at} shortDate /> : "-",
                locations: <LocationsCell locations={p.locations} />,
                status: <BreakableBadge breakable={p.breakable_stock_available} t={t} />,
            }))
            : slice.map((o) => ({
                object: { id: o.order_id },
                _raw: o,
                order_number: o.order_number,
                total_pieces: o.total_pieces,
                products_count: o.products_count,
                locations: <LocationsCell locations={o.locations} />,
            }));

        return {
            pageRows: rows,
            paginationMeta: { current_page: currentPage, per_page: perPage, total_count: totalCount, total_pages: totalPages },
        };
    }, [products, orderRows, pivot, appliedFilters, sortBy, page, perPage, i18n]); // eslint-disable-line react-hooks/exhaustive-deps

    const switchPivot = (nextPivot) => {
        if (nextPivot === pivot) return;
        const next = initialFiltersData(nextPivot);
        setPivot(nextPivot);
        setFiltersData(next);
        setAppliedFilters(next);
        setSortBy([]);
        setPage(1);
    };

    const onSearchHandler = (updatedData, filterKey) => {
        setFiltersData((prev) => prev.map((item) => (item.key === filterKey ? { ...item, data: updatedData } : item)));
    };

    // Filters apply only on "Aplicar" (manual). On entry / pivot switch the
    // applied set is the pivot default (no active filters -> shows everything).
    const handleApplyFilters = () => {
        setAppliedFilters(filtersData);
        setPage(1);
    };

    const handleResetFilters = () => {
        const next = initialFiltersData(pivot);
        setFiltersData(next);
        setAppliedFilters(next);
        setPage(1);
    };

    const openDetail = (raw) => {
        setDetail({ pivot, raw });
        setDetailOpen(true);
    };

    const pivotButton = (value, label) => (
        <button
            type="button"
            onClick={() => switchPivot(value)}
            className={`px-3 py-1.5 text-sm font-medium border ${
                pivot === value
                    ? "bg-indigo-600 text-white border-indigo-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
            } ${value === PIVOTS.PRODUCT ? "rounded-l-md" : "rounded-r-md -ml-px"}`}
        >
            {label}
        </button>
    );

    return (
        <PageView
            topMenu={
                <PageTopBar>
                    <div className="text-lg font-semibold">{t(`${T}.title`)}</div>
                    <button
                        type="button"
                        className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-900 disabled:opacity-50"
                        onClick={() => refetch()}
                        disabled={isFetching || !hasPermission}
                    >
                        <RefreshIcon className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
                        {t(`${T}.refresh`)}
                    </button>
                </PageTopBar>
            }
            childrenFullWidth={true}
            topMenuFullWidth={true}
        >
            {!hasPermission || (isError && error?.response?.status === 403) ? (
                <div className="p-6 text-gray-500">{t(`${T}.forbidden`)}</div>
            ) : isLoading ? (
                <>{t("common.loading")}</>
            ) : isError ? (
                <>Error: {error?.message}</>
            ) : (
                <>
                    <div className="flex items-center justify-between mb-3">
                        <div className="inline-flex">
                            {pivotButton(PIVOTS.PRODUCT, t(`${T}.pivot.by_product`))}
                            {pivotButton(PIVOTS.ORDER, t(`${T}.pivot.by_order`))}
                        </div>
                        <div className="text-sm text-gray-500">
                            {t(`${T}.summary`, { products: summaryProducts, orders: summaryOrders })}
                        </div>
                    </div>

                    <NewTable
                        key={pivot}
                        columns={pivot === PIVOTS.PRODUCT ? productColumns : orderColumns}
                        data={pageRows}
                        cursorPointer
                        showHeader
                        showPaginationOnFooter
                        footerText={pivot === PIVOTS.PRODUCT ? t(`${T}.footer_products`) : t(`${T}.footer_orders`)}
                        isFetching={isFetching}
                        showLoader={isFetching}
                        emptyTableText={t(`${T}.empty_table_text`)}
                        filtersData={filtersData}
                        onSearchHandler={onSearchHandler}
                        handleResetFilters={handleResetFilters}
                        applyFiltersManually
                        onManuallyAppliedFilters={handleApplyFilters}
                        onSortChange={setSortBy}
                        paginationMeta={paginationMeta}
                        onPaginationChange={(nextPage) => setPage(nextPage)}
                        onPageSizeChange={(size) => {
                            setPerPage(parseInt(size));
                            setPage(1);
                        }}
                        rowProps={(row) => ({ onClick: () => openDetail(row.original._raw) })}
                    />

                    <DialogView open={detailOpen} setOpen={setDetailOpen} closeOnBackdrop className="w-full">
                        <DetailContent detail={detail} t={t} />
                    </DialogView>
                </>
            )}
        </PageView>
    );
};

const ItemsList = ({ items, t }) => {
    if (!items || items.length === 0) return null;
    return (
        <details className="mt-1">
            <summary className="cursor-pointer text-xs text-indigo-600">{t(`${T}.detail.boxes_title`)}</summary>
            <ul className="mt-1 pl-4 text-xs text-gray-500 space-y-0.5">
                {items.map((it, idx) => (
                    <li key={`${it.container_id}-${idx}`}>
                        {it.container_barcode || <span className="italic">{t(`${T}.detail.no_barcode`)}</span>}
                        {" · "}
                        {it.location || "-"}
                        {" · "}
                        {t(`${T}.detail.pieces_short`, { count: it.pieces })}
                    </li>
                ))}
            </ul>
        </details>
    );
};

const DetailContent = ({ detail, t }) => {
    if (!detail) return null;
    const { pivot, raw } = detail;

    if (pivot === PIVOTS.PRODUCT) {
        return (
            <div className="max-h-[70vh] overflow-y-auto">
                <div className="flex items-center gap-2 mb-1">
                    <h2 className="text-lg font-semibold">{raw.product_name}</h2>
                    <BreakableBadge breakable={raw.breakable_stock_available} t={t} />
                </div>
                <p className="text-sm text-gray-500 mb-4">
                    {t(`${T}.detail.product_summary`, {
                        pieces: raw.pieces_needed,
                        orders: raw.affected_orders,
                    })}
                </p>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">{t(`${T}.detail.orders_title`)}</h3>
                <table className="w-full text-sm">
                    <thead className="text-left text-gray-500 border-b">
                        <tr>
                            <th className="py-1 pr-3">{t(`${T}.headers.order_number`)}</th>
                            <th className="py-1 pr-3">{t(`${T}.headers.store`)}</th>
                            <th className="py-1 pr-3">{t(`${T}.detail.pieces`)}</th>
                            <th className="py-1">{t(`${T}.headers.locations`)}</th>
                        </tr>
                    </thead>
                    <tbody className="align-top divide-y">
                        {(raw.orders || []).map((o) => {
                            const locs = [...new Set((o.items || []).map((i) => i.location).filter(Boolean))].sort();
                            return (
                                <tr key={o.order_id}>
                                    <td className="py-1.5 pr-3 font-medium">{o.order_number}</td>
                                    <td className="py-1.5 pr-3">{o.store_name || "-"}</td>
                                    <td className="py-1.5 pr-3">{o.pieces_needed}</td>
                                    <td className="py-1.5">
                                        <LocationsCell locations={locs} />
                                        <ItemsList items={o.items} t={t} />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    }

    return (
        <div className="max-h-[70vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-1">{raw.order_number}</h2>
            <p className="text-sm text-gray-500 mb-4">
                {t(`${T}.detail.order_summary`, { pieces: raw.total_pieces, products: raw.products_count })}
            </p>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">{t(`${T}.detail.products_title`)}</h3>
            <table className="w-full text-sm">
                <thead className="text-left text-gray-500 border-b">
                    <tr>
                        <th className="py-1 pr-3">{t(`${T}.headers.product`)}</th>
                        <th className="py-1 pr-3">{t(`${T}.detail.pieces`)}</th>
                        <th className="py-1 pr-3">{t(`${T}.headers.status`)}</th>
                        <th className="py-1">{t(`${T}.headers.locations`)}</th>
                    </tr>
                </thead>
                <tbody className="align-top divide-y">
                    {(raw.products || []).map((p) => (
                        <tr key={p.product_id}>
                            <td className="py-1.5 pr-3"><ProductNameCell product={p} /></td>
                            <td className="py-1.5 pr-3">{p.pieces_needed}</td>
                            <td className="py-1.5 pr-3"><BreakableBadge breakable={p.breakable_stock_available} t={t} /></td>
                            <td className="py-1.5">
                                <LocationsCell locations={p.locations} />
                                <ItemsList items={p.items} t={t} />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};
